import {
  ContactRelationship,
  ContactType,
  CustomerType,
  getPrincipleAri,
  getResourceAri,
  PatchOperationPathType,
  PatchOperationType,
} from './constant';

interface createContactServicePayload {
  invoiceGroupId: string;
  transactionAccountId: string;
  createContactIds: string[];
  primaryContactId?: string;
  createBillingContactIds?: string[];
}
interface deleteContactServicePayload {
  invoiceGroupId: string;
  transactionAccountId: string;
  deleteContactIds: string[];
  deleteBillingContactIds?: string[];
}

interface patchPayloadForSetPrimaryContactArgs {
  invoiceGroupId: string;
  transactionAccountId: string;
  primaryContactId: string;
}

interface patchPayloadForSetAndDeletePrimaryContactArgs {
  invoiceGroupId: string;
  transactionAccountId: string;
  newPrimaryContactId: string;
  existingPrimaryContactId: string;
}

export const useContactServicePayload = () => {
  const getCreateContactRelationshipsPayload = ({
    invoiceGroupId,
    transactionAccountId,
    createContactIds,
    primaryContactId = '',
    createBillingContactIds,
  }: createContactServicePayload) => {
    const resourceAri = getResourceAri(transactionAccountId, invoiceGroupId);
    let createContactRelationshipsPayload = {};
    const hasCustomerContactIds = createContactIds && createContactIds.length > 0;
    const hasBillingContactIds =
      createBillingContactIds && createBillingContactIds?.length > 0 && createBillingContactIds[0] !== '';
    if (hasCustomerContactIds || hasBillingContactIds) {
      let createCustomerContactRelationships: ContactRelationship[] = [];
      let createBillingContactRelationships: ContactRelationship[] = [];
      if (hasCustomerContactIds) {
        createCustomerContactRelationships = createContactIds?.map((id) => {
          return {
            resourceAri,
            contactType: ContactType.CUSTOMER_CONTACT,
            principalAri: getPrincipleAri(id),
            ...(primaryContactId === id && { isPrimary: true }),
          };
        });
      }

      if (hasBillingContactIds) {
        createBillingContactRelationships = createBillingContactIds?.map((id) => {
          return {
            resourceAri,
            contactType: ContactType.BILLING_CONTACT,
            principalAri: getPrincipleAri(id),
          };
        });
      }

      const createContactRelationships = [...createCustomerContactRelationships, ...createBillingContactRelationships];

      createContactRelationshipsPayload = {
        payload: {
          customerType: CustomerType.PARTNER,
          contactRelationships: createContactRelationships,
        },
        transactionAccountId: transactionAccountId,
      };
    }

    return createContactRelationshipsPayload;
  };

  const getDeleteContactRelationshipsPayload = ({
    invoiceGroupId,
    transactionAccountId,
    deleteContactIds,
    deleteBillingContactIds,
  }: deleteContactServicePayload) => {
    let deleteContactRelationshipsPayload = {};
    const hasCustomerContactIds = deleteContactIds && deleteContactIds.length > 0;
    const hasBillingContactIds = deleteBillingContactIds && deleteBillingContactIds?.length > 0;

    const defaultPayload = {
      resourceAri: getResourceAri(transactionAccountId, invoiceGroupId),
      customerType: CustomerType.PARTNER,
    };

    if (hasCustomerContactIds || hasBillingContactIds) {
      let deleteCustomerContactRelationships: ContactRelationship[] = [];
      let deleteBillingContactRelationships: ContactRelationship[] = [];
      if (hasCustomerContactIds) {
        deleteCustomerContactRelationships = deleteContactIds.map((id) => {
          return {
            ...defaultPayload,
            contactType: ContactType.CUSTOMER_CONTACT,
            principalAri: getPrincipleAri(id),
          };
        });
      }

      if (hasBillingContactIds) {
        deleteBillingContactRelationships = deleteBillingContactIds?.map((id) => {
          return {
            ...defaultPayload,
            contactType: ContactType.BILLING_CONTACT,
            principalAri: getPrincipleAri(id),
          };
        });
      }

      const deleteContactRelationships = [...deleteCustomerContactRelationships, ...deleteBillingContactRelationships];

      deleteContactRelationshipsPayload = {
        payload: deleteContactRelationships,
        transactionAccountId: transactionAccountId,
      };
    }

    return deleteContactRelationshipsPayload;
  };

  const getPatchPayloadForSetPrimaryContact = ({
    invoiceGroupId,
    transactionAccountId,
    primaryContactId,
  }: patchPayloadForSetPrimaryContactArgs) => {
    const resourceAri = getResourceAri(transactionAccountId, invoiceGroupId);
    return {
      payload: [
        {
          op: PatchOperationType.ADD,
          path: PatchOperationPathType.PRIMARY,
          value: true,
          filter: {
            resourceAri,
            principalAri: getPrincipleAri(primaryContactId),
            contactType: ContactType.CUSTOMER_CONTACT,
          },
        },
      ],
      transactionAccountId,
    };
  };

  const getPatchPayloadForAddingAndRemovingPrimaryContact = ({
    invoiceGroupId,
    transactionAccountId,
    newPrimaryContactId,
    existingPrimaryContactId,
  }: patchPayloadForSetAndDeletePrimaryContactArgs) => {
    const resourceAri = getResourceAri(transactionAccountId, invoiceGroupId);
    return {
      payload: [
        {
          op: PatchOperationType.ADD,
          path: PatchOperationPathType.PRIMARY,
          value: true,
          filter: {
            resourceAri,
            principalAri: getPrincipleAri(newPrimaryContactId),
            contactType: ContactType.CUSTOMER_CONTACT,
          },
        },
        {
          op: PatchOperationType.REMOVE,
          path: PatchOperationPathType.PRIMARY,
          value: false,
          filter: {
            resourceAri,
            principalAri: getPrincipleAri(existingPrimaryContactId),
            contactType: ContactType.CUSTOMER_CONTACT,
          },
        },
      ],
      transactionAccountId,
    };
  };

  return {
    getCreateContactRelationshipsPayload,
    getDeleteContactRelationshipsPayload,
    getPatchPayloadForSetPrimaryContact,
    getPatchPayloadForAddingAndRemovingPrimaryContact,
  };
};
